interface GeminiTopic {
  subtopics: {
    [key: string]: string[];
  };
}

interface GeminiResponse {
  topics: {
    [key: string]: GeminiTopic;
  };
}

interface Subtopic {
  name: string;
  keywords: string[];
}

interface Topic {
  name: string;
  subtopics: Subtopic[];
}

export interface AnalysisData {
  topics: {
    name: string;
    subtopics: {
      name: string;
      keywords: string[];
    }[];
  }[];
}

export const transformAnalysisResults = (response: any): AnalysisData => {
  // Create a default analysis data structure
  const defaultData: AnalysisData = {
    topics: []
  };

  // If the response is null or undefined, return the default
  if (!response) {
    return defaultData;
  }

  // Special case for nested response formats
  if (response.results?.content?.topics) {
    return transformAnalysisResults(response.results.content);
  }

  // If the response already has a topics array, use it but ensure proper structure
  if (response.topics && Array.isArray(response.topics)) {
    // Make sure all required properties are present in the topics
    const processedTopics = response.topics.map((topic: any) => {
      return {
        name: topic.name || 'Unnamed Topic',
        subtopics: (topic.subtopics || []).map((subtopic: any) => {
          return {
            name: subtopic.name || 'Unnamed Subtopic',
            keywords: Array.isArray(subtopic.keywords) ? subtopic.keywords : []
          };
        })
      };
    });
    
    // Return in the expected format
    return {
      topics: processedTopics
    };
  }

  // If the response has results with topics object
  if (response.results && response.results.topics && typeof response.results.topics === 'object') {
    // Convert results.topics object to topics array format
    const topicsArray = Object.entries(response.results.topics).map(([topicName, topicData]: [string, any]) => {
      const subtopics = Object.entries(topicData.subtopics || {}).map(([subtopicName, subtopicData]: [string, any]) => {
        // Handle different formats of subtopic data
        let keywords = [];
        if (Array.isArray(subtopicData)) {
          // Old format: subtopicData is an array of stem words - ignore it
          keywords = [];
        } else if (subtopicData && typeof subtopicData === 'object' && subtopicData.keywords) {
          // New format: subtopicData is an object with keywords
          keywords = subtopicData.keywords;
        }
        
        return {
          name: subtopicName,
          keywords: keywords
        };
      });

      return {
        name: topicName,
        subtopics
      };
    });

    return {
      topics: topicsArray
    };
  }

  // Handle other nested formats like response.topics as object not array
  if (response.topics && typeof response.topics === 'object' && !Array.isArray(response.topics)) {
    const topicsArray = Object.entries(response.topics).map(([topicName, topicData]: [string, any]) => {
      const subtopics = Object.entries(topicData.subtopics || {}).map(([subtopicName, subtopicData]: [string, any]) => {
        // Support both formats: subtopicData could be an array of stem words or an object with stem_words/keywords
        if (Array.isArray(subtopicData)) {
          // Old format: subtopicData is an array of stem words
          return {
            name: subtopicName,
            stem_words: subtopicData,
            keywords: [] // No keywords in old format
          };
        } else if (typeof subtopicData === 'object') {
          // New format: subtopicData is an object with stem_words and keywords
          return {
            name: subtopicName,
            stem_words: Array.isArray(subtopicData.stem_words) ? subtopicData.stem_words : [],
            keywords: Array.isArray(subtopicData.keywords) ? subtopicData.keywords : []
          };
        } else {
          // Fallback for unexpected format
          return {
            name: subtopicName,
            stem_words: [],
            keywords: []
          };
        }
      });

      return {
        name: topicName,
        subtopics
      };
    });

    return {
      topics: topicsArray,
      missing_themes: response.missing_themes || []
    };
  }

  // Default case - no matching format found
  return defaultData;
};

// Helper function to convert UI format back to Gemini format
export function convertToGeminiFormat(topics: Topic[]): GeminiResponse {
  const geminiTopics: { [key: string]: GeminiTopic } = {};

  for (const topic of topics) {
    const subtopics: { [key: string]: string[] } = {};

    for (const subtopic of topic.subtopics) {
      subtopics[subtopic.name] = subtopic.stemWords.map(sw => sw.word);
    }

    geminiTopics[topic.name] = { subtopics };
  }

  return {
    topics: geminiTopics,
    missing_themes: [] // This would be handled separately
  };
}
